import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Chip,
  Input,
  Select,
  SelectItem,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  Tooltip,
} from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import {
  getSuperadminShipsGoTrackingOperations,
  type ShipsGoJourneyOperations,
  type ShipsGoOperationsPayload,
  type ShipsGoRunStatus,
  type ShipsGoWorkerRun,
  type ShipsGoWorkerType,
} from 'common/src/api/shipsgo-tracking-operations';
import { Alert, KpiCard, PageHeader, PageLayout } from '../../../components/ui';

const PAGE_SIZE = 50;

const workerDefinitions: Array<{
  type: ShipsGoWorkerType;
  label: string;
  schedule: string;
  description: string;
  maxAgeHours: number;
  icon: string;
}> = [
  {
    type: 'discovery',
    label: 'Descubrimiento',
    schedule: 'Diario · 09:00 UTC',
    description: 'Sincroniza fuentes y crea o reutiliza journeys elegibles.',
    maxAgeHours: 28,
    icon: 'tabler:radar',
  },
  {
    type: 'registration',
    label: 'Registro ShipsGo',
    schedule: 'Cada hora · minuto 30',
    description: 'Registra hasta 10 identificadores de forma secuencial.',
    maxAgeHours: 2.5,
    icon: 'tabler:route-square',
  },
  {
    type: 'observation',
    label: 'Observación',
    schedule: 'Cada 10 min · máximo 1 consulta/h por journey',
    description:
      'Prioriza datos pendientes y actualiza el ciclo del contenedor.',
    maxAgeHours: 0.5,
    icon: 'tabler:refresh-dot',
  },
];

const statusOptions = [
  { value: 'scheduled', label: 'Programado' },
  { value: 'queued', label: 'En cola' },
  { value: 'registering', label: 'Registrando' },
  { value: 'retry_pending', label: 'Reintento pendiente' },
  { value: 'active', label: 'Activo' },
  { value: 'completed', label: 'Completado' },
  { value: 'window_expired', label: 'Ventana expirada' },
  { value: 'rejected', label: 'Rechazado' },
  { value: 'failed', label: 'Fallido' },
  { value: 'cancelled', label: 'Cancelado' },
];

function number(value: number | null | undefined): string {
  return Number(value ?? 0).toLocaleString('es-CL');
}

function errorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (
    error &&
    typeof error === 'object' &&
    'message' in error &&
    typeof error.message === 'string'
  ) {
    return error.message;
  }
  return 'No se pudo cargar la operación ShipsGo';
}

function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const parsedValue = /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? new Date(`${value}T12:00:00`)
    : new Date(value);
  return new Intl.DateTimeFormat('es-CL', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(parsedValue);
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) return 'Nunca';
  return new Intl.DateTimeFormat('es-CL', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value));
}

function relativeTime(value: string | null | undefined): string {
  if (!value) return 'Nunca';
  const minutes = Math.max(
    0,
    Math.round((Date.now() - new Date(value).getTime()) / 60_000)
  );
  if (minutes < 2) return 'Hace un momento';
  if (minutes < 60) return `Hace ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `Hace ${hours} h`;
  return `Hace ${Math.round(hours / 24)} días`;
}

function duration(run: ShipsGoWorkerRun): string {
  if (!run.completed_at) return 'En curso';
  const milliseconds = Math.max(
    0,
    new Date(run.completed_at).getTime() - new Date(run.started_at).getTime()
  );
  if (milliseconds < 1_000) return `${milliseconds} ms`;
  if (milliseconds < 60_000) return `${Math.round(milliseconds / 1_000)} s`;
  return `${Math.round(milliseconds / 60_000)} min`;
}

function runColor(status: ShipsGoRunStatus) {
  if (status === 'succeeded') return 'success' as const;
  if (status === 'partial') return 'warning' as const;
  if (status === 'running') return 'primary' as const;
  return 'danger' as const;
}

function journeyColor(status: string) {
  if (status === 'active' || status === 'completed') return 'success' as const;
  if (status === 'scheduled' || status === 'queued') return 'primary' as const;
  if (status === 'registering') return 'secondary' as const;
  if (status === 'retry_pending' || status === 'window_expired') {
    return 'warning' as const;
  }
  if (status === 'failed' || status === 'rejected') return 'danger' as const;
  return 'default' as const;
}

function observationLabel(status: string): string {
  const labels: Record<string, string> = {
    pending: 'Pendiente',
    observing: 'Observando',
    cargo_arrived: 'Carga arribada',
    gate_out: 'Gate out',
    container_returned: 'Vacío devuelto',
    closed: 'Cerrado',
  };
  return labels[status] ?? status;
}

function statusLabel(status: string): string {
  return (
    statusOptions.find((option) => option.value === status)?.label ?? status
  );
}

function runHealth(
  run: ShipsGoWorkerRun | undefined,
  maxAgeHours: number
): {
  label: string;
  color: 'success' | 'warning' | 'danger' | 'primary';
} {
  if (!run) return { label: 'Sin ejecuciones', color: 'warning' };
  if (run.status === 'failed') return { label: 'Falló', color: 'danger' };
  if (run.status === 'partial') return { label: 'Parcial', color: 'warning' };
  const ageHours =
    (Date.now() - new Date(run.started_at).getTime()) / (60 * 60 * 1_000);
  if (run.status === 'running') {
    return ageHours > maxAgeHours
      ? { label: 'Atascado', color: 'danger' }
      : { label: 'Ejecutando', color: 'primary' };
  }
  if (ageHours > maxAgeHours) return { label: 'Atrasado', color: 'warning' };
  return { label: 'Operativo', color: 'success' };
}

function metadataCounts(run: ShipsGoWorkerRun): string | null {
  if (run.worker_type === 'discovery') {
    const counts = run.metadata?.counts as Record<string, unknown> | undefined;
    if (!counts) return null;
    return `Elegibles ${number(Number(counts.eligible ?? 0))} · creados ${number(
      Number(counts.journeys_created ?? 0)
    )} · reutilizados ${number(Number(counts.journeys_reused ?? 0))}`;
  }
  if (run.worker_type === 'registration') {
    return run.metadata?.budget_exhausted
      ? 'Se alcanzó el presupuesto de ejecución'
      : null;
  }
  return null;
}

const ShipsGoTrackingOperationsPage = () => {
  const [data, setData] = useState<ShipsGoOperationsPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [exporterId, setExporterId] = useState('');
  const [status, setStatus] = useState('');
  const [mode, setMode] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const requestSequenceRef = useRef(0);

  const loadData = useCallback(async () => {
    const requestSequence = ++requestSequenceRef.current;
    setLoading(true);
    try {
      const response = await getSuperadminShipsGoTrackingOperations({
        exporterId: exporterId ? Number(exporterId) : undefined,
        status: status || undefined,
        mode: mode === 'ocean' || mode === 'air' ? mode : undefined,
        search: search || undefined,
        limit: PAGE_SIZE,
        offset,
      });
      if (requestSequence === requestSequenceRef.current) {
        setData(response);
      }
    } catch (error) {
      if (requestSequence !== requestSequenceRef.current) return;
      console.error('Could not load ShipsGo operations', error);
      toast.error(errorMessage(error));
    } finally {
      if (requestSequence === requestSequenceRef.current) {
        setLoading(false);
      }
    }
  }, [exporterId, mode, offset, search, status]);

  useEffect(() => {
    void loadData();
    return () => {
      requestSequenceRef.current += 1;
    };
  }, [loadData]);

  const latestScheduledRun = useMemo(() => {
    const result = new Map<ShipsGoWorkerType, ShipsGoWorkerRun>();
    for (const run of data?.runs ?? []) {
      if (run.trigger_source !== 'scheduled' || result.has(run.worker_type)) {
        continue;
      }
      result.set(run.worker_type, run);
    }
    return result;
  }, [data?.runs]);

  const submitSearch = () => {
    setOffset(0);
    setSearch(searchInput.trim());
  };

  const total = data?.pagination.total ?? 0;
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const renderJourney = (journey: ShipsGoJourneyOperations) => {
    const linkLabels = journey.links
      .map((link) => link.reference_number)
      .filter((value): value is string => Boolean(value));
    const hasAttention =
      journey.provider_data_attention ||
      journey.consecutive_sync_failures > 0 ||
      ['retry_pending', 'failed', 'rejected'].includes(journey.status);

    return (
      <TableRow key={journey.id}>
        <TableCell>
          <div className='min-w-44'>
            <p className='font-medium text-default-800'>
              {journey.exporter_name}
            </p>
            <p className='font-mono text-sm font-semibold text-default-900'>
              {journey.identifier_value}
            </p>
            <div className='mt-1 flex gap-1'>
              <Chip size='sm' variant='flat'>
                {journey.mode === 'ocean' ? 'Contenedor' : 'AWB'}
              </Chip>
              <span className='text-xs text-default-400'>
                {journey.id.slice(0, 8)}
              </span>
            </div>
          </div>
        </TableCell>
        <TableCell>
          <div className='flex min-w-36 flex-col items-start gap-1'>
            <Chip
              size='sm'
              color={
                journey.provider_data_attention
                  ? 'danger'
                  : journey.waiting_for_provider_data
                    ? 'warning'
                    : journeyColor(journey.status)
              }
              variant='flat'
            >
              {journey.waiting_for_provider_data
                ? 'Esperando datos ShipsGo'
                : statusLabel(journey.status)}
            </Chip>
            <span className='text-xs text-default-500'>
              {journey.waiting_for_provider_data
                ? 'Monitoreo activo'
                : observationLabel(journey.observation_status)}
            </span>
            {journey.provider_container_status && (
              <span className='text-xs font-medium text-default-600'>
                Equipo: {journey.provider_container_status}
              </span>
            )}
          </div>
        </TableCell>
        <TableCell>
          <div className='min-w-40 text-sm'>
            <p className='font-mono text-xs text-default-700'>
              {journey.provider_tracking_id ?? 'Aún sin ID ShipsGo'}
            </p>
            <p className='mt-1 text-xs text-default-500'>
              {journey.carrier_name ??
                journey.carrier_code ??
                'Carrier pendiente'}
            </p>
            {journey.provider_status && (
              <p className='text-xs text-default-500'>
                Viaje: {journey.provider_status}
              </p>
            )}
          </div>
        </TableCell>
        <TableCell>
          <div className='min-w-44 text-xs'>
            <p className='font-medium text-default-700'>
              {journey.origin_name ?? 'Origen pendiente'}
            </p>
            <p className='my-0.5 text-default-400'>→</p>
            <p className='font-medium text-default-700'>
              {journey.destination_name ?? 'Destino pendiente'}
            </p>
            <p className='mt-1 text-default-500'>
              ETD {formatDate(journey.live_etd ?? journey.planned_etd)} · ETA{' '}
              {formatDate(journey.live_eta ?? journey.planned_eta)}
            </p>
          </div>
        </TableCell>
        <TableCell>
          <div className='min-w-48'>
            {linkLabels.length > 0 ? (
              <div className='flex flex-wrap gap-1'>
                {linkLabels.slice(0, 3).map((reference, index) => (
                  <Chip
                    key={`${reference}-${index}`}
                    size='sm'
                    variant='bordered'
                  >
                    {reference}
                  </Chip>
                ))}
                {linkLabels.length > 3 && (
                  <Chip size='sm' variant='flat'>
                    +{linkLabels.length - 3}
                  </Chip>
                )}
              </div>
            ) : (
              <p className='text-xs text-warning-600'>Sin vínculo vigente</p>
            )}
            <p className='mt-1 text-xs text-default-500'>
              {journey.links[0]?.species_name ?? 'Especie sin vínculo'} ·{' '}
              {journey.links[0]?.season ?? 'temporada —'}
            </p>
          </div>
        </TableCell>
        <TableCell>
          <div className='min-w-44 text-xs'>
            <p className={hasAttention ? 'font-medium text-danger' : ''}>
              {journey.provider_data_attention
                ? 'ShipsGo sigue en NEW sin eventos'
                : journey.waiting_for_provider_data
                  ? 'Esperando primera ruta o evento'
                  : hasAttention
                    ? `${journey.consecutive_sync_failures} fallo(s) consecutivo(s)`
                    : `${number(journey.actual_event_count)} eventos reales`}
            </p>
            <p className='mt-1 text-default-500'>
              Último sync: {relativeTime(journey.last_sync_succeeded_at)}
            </p>
            <p className='text-default-500'>
              Registro: {number(journey.registration_attempt_count)} intento(s)
            </p>
            {journey.last_error_message && (
              <Tooltip content={journey.last_error_message}>
                <p className='mt-1 max-w-44 truncate text-danger'>
                  {journey.last_error_code ?? journey.last_error_message}
                </p>
              </Tooltip>
            )}
          </div>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <PageLayout className='flex flex-col gap-6'>
      <PageHeader
        title='Operación ShipsGo'
        description={
          <>
            Salud de automatizaciones, consumo de créditos y ciclo individual de
            contenedores y AWB. Esta vista no mezcla datos del tracking FindTEU.
            {data?.generated_at && (
              <span className='mt-1 block text-xs text-default-400'>
                Datos generados {formatDateTime(data.generated_at)}
              </span>
            )}
          </>
        }
        actions={
          <Button
            variant='flat'
            startContent={<Icon icon='tabler:refresh' width={18} />}
            onPress={() => void loadData()}
            isLoading={loading}
          >
            Actualizar
          </Button>
        }
      />

      <section className='grid gap-4 lg:grid-cols-3'>
        {workerDefinitions.map((worker) => {
          const run = latestScheduledRun.get(worker.type);
          const health = runHealth(run, worker.maxAgeHours);
          return (
            <Card key={worker.type} shadow='sm'>
              <CardHeader className='flex items-start justify-between gap-3 px-5 pt-5'>
                <div className='flex items-start gap-3'>
                  <span className='rounded-xl bg-primary-50 p-2 text-primary'>
                    <Icon icon={worker.icon} width={22} />
                  </span>
                  <div>
                    <h2 className='font-semibold'>{worker.label}</h2>
                    <p className='text-xs text-default-500'>
                      {worker.schedule}
                    </p>
                  </div>
                </div>
                <Chip size='sm' color={health.color} variant='flat'>
                  {health.label}
                </Chip>
              </CardHeader>
              <CardBody className='px-5 pb-5 pt-2'>
                <p className='text-sm text-default-500'>{worker.description}</p>
                {run ? (
                  <div className='mt-3 rounded-lg bg-default-50 p-3 text-xs'>
                    <div className='flex justify-between gap-3'>
                      <span>{relativeTime(run.started_at)}</span>
                      <span>{duration(run)}</span>
                    </div>
                    <p className='mt-1 text-default-500'>
                      Seleccionados {number(run.selected_count)} · OK{' '}
                      {number(run.succeeded_count)} · Fallidos{' '}
                      {number(run.failed_count)}
                    </p>
                    {metadataCounts(run) && (
                      <p className='mt-1 text-default-500'>
                        {metadataCounts(run)}
                      </p>
                    )}
                    {run.error_message && (
                      <p className='mt-1 text-danger'>{run.error_message}</p>
                    )}
                  </div>
                ) : (
                  <Alert variant='warning' className='mt-3'>
                    No hay evidencia de una ejecución programada todavía.
                  </Alert>
                )}
              </CardBody>
            </Card>
          );
        })}
      </section>

      <section className='grid gap-3 sm:grid-cols-2 xl:grid-cols-6'>
        <KpiCard
          label='Journeys'
          value={number(data?.summary.total)}
          detail={`${number(data?.summary.ocean)} ocean · ${number(data?.summary.air)} air`}
          icon='tabler:route'
        />
        <KpiCard
          label='Registrados'
          value={number(data?.summary.registered)}
          detail={`${number(data?.summary.queued)} en cola o reintento`}
          icon='tabler:circle-check'
        />
        <KpiCard
          label='Observando'
          value={number(data?.summary.observing)}
          detail={`${number(
            data?.summary.waiting_provider_data
          )} esperando datos ShipsGo`}
          icon='tabler:eye'
        />
        <KpiCard
          label='Vacíos devueltos'
          value={number(data?.summary.returned)}
          detail={`${number(data?.summary.completed)} journeys completados`}
          icon='tabler:container'
        />
        <KpiCard
          label='Requieren atención'
          value={number(data?.summary.attention)}
          detail='Fallos o datos ShipsGo demorados'
          icon='tabler:alert-triangle'
          tone={data?.summary.attention ? 'danger' : 'default'}
        />
        <KpiCard
          label='Créditos ShipsGo'
          value={
            data?.credits.latest_credits_remaining == null
              ? '—'
              : number(data.credits.latest_credits_remaining)
          }
          detail={`${number(data?.credits.credits_consumed_30d)} consumidos en 30 días`}
          icon='tabler:coins'
        />
      </section>

      <Card shadow='sm'>
        <CardHeader className='flex-col items-start gap-1 px-5 pt-5'>
          <h2 className='text-lg font-semibold'>Por exportadora</h2>
          <p className='text-sm text-default-500'>
            Inventario global de journeys y alertas, independiente de los
            filtros de la tabla inferior.
          </p>
        </CardHeader>
        <CardBody className='overflow-x-auto px-0 pb-0'>
          <Table
            removeWrapper
            aria-label='Operación ShipsGo por exportadora'
            classNames={{ th: 'bg-default-50' }}
          >
            <TableHeader>
              <TableColumn>EXPORTADORA</TableColumn>
              <TableColumn align='end'>TOTAL</TableColumn>
              <TableColumn align='end'>REGISTRADOS</TableColumn>
              <TableColumn align='end'>OBSERVANDO</TableColumn>
              <TableColumn align='end'>ESPERANDO DATOS</TableColumn>
              <TableColumn align='end'>DEVUELTOS</TableColumn>
              <TableColumn align='end'>ATENCIÓN</TableColumn>
              <TableColumn>ÚLTIMA ACTIVIDAD</TableColumn>
            </TableHeader>
            <TableBody
              emptyContent={
                loading ? <Spinner size='sm' /> : 'Todavía no existen journeys.'
              }
            >
              {(data?.exporters ?? []).map((exporter) => (
                <TableRow key={exporter.exporter_id}>
                  <TableCell>
                    <button
                      className='text-left font-medium text-primary hover:underline'
                      onClick={() => {
                        setExporterId(String(exporter.exporter_id));
                        setOffset(0);
                      }}
                    >
                      {exporter.exporter_name}
                    </button>
                  </TableCell>
                  <TableCell>{number(exporter.total)}</TableCell>
                  <TableCell>{number(exporter.registered)}</TableCell>
                  <TableCell>{number(exporter.observing)}</TableCell>
                  <TableCell>
                    {number(exporter.waiting_provider_data)}
                  </TableCell>
                  <TableCell>{number(exporter.returned)}</TableCell>
                  <TableCell>
                    <Chip
                      size='sm'
                      color={exporter.attention > 0 ? 'danger' : 'success'}
                      variant='flat'
                    >
                      {number(exporter.attention)}
                    </Chip>
                  </TableCell>
                  <TableCell>
                    {relativeTime(exporter.last_activity_at)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardBody>
      </Card>

      <Card shadow='sm'>
        <CardHeader className='flex-col items-start gap-4 px-5 pt-5'>
          <div>
            <h2 className='text-lg font-semibold'>
              Journeys e identificadores
            </h2>
            <p className='text-sm text-default-500'>
              Una fila por contenedor o AWB registrado en su ventana de viaje.
            </p>
          </div>
          <div className='grid w-full gap-3 md:grid-cols-2 xl:grid-cols-5'>
            <Select
              label='Exportadora'
              placeholder='Todas'
              selectedKeys={exporterId ? [exporterId] : []}
              onChange={(event) => {
                setExporterId(event.target.value);
                setOffset(0);
              }}
            >
              {(data?.exporters ?? []).map((exporter) => (
                <SelectItem
                  key={String(exporter.exporter_id)}
                  textValue={exporter.exporter_name}
                >
                  {exporter.exporter_name}
                </SelectItem>
              ))}
            </Select>
            <Select
              label='Estado'
              placeholder='Todos'
              selectedKeys={status ? [status] : []}
              onChange={(event) => {
                setStatus(event.target.value);
                setOffset(0);
              }}
            >
              {statusOptions.map((option) => (
                <SelectItem key={option.value}>{option.label}</SelectItem>
              ))}
            </Select>
            <Select
              label='Modo'
              placeholder='Todos'
              selectedKeys={mode ? [mode] : []}
              onChange={(event) => {
                setMode(event.target.value);
                setOffset(0);
              }}
            >
              <SelectItem key='ocean'>Ocean / contenedor</SelectItem>
              <SelectItem key='air'>Air / AWB</SelectItem>
            </Select>
            <Input
              className='xl:col-span-2'
              label='Buscar'
              placeholder='Contenedor, AWB, referencia, ShipsGo ID…'
              value={searchInput}
              onValueChange={setSearchInput}
              onKeyDown={(event) => {
                if (event.key === 'Enter') submitSearch();
              }}
              endContent={
                <Button
                  isIconOnly
                  size='sm'
                  variant='light'
                  onPress={submitSearch}
                >
                  <Icon icon='tabler:search' width={18} />
                </Button>
              }
            />
          </div>
          {(exporterId || status || mode || search) && (
            <Button
              size='sm'
              variant='light'
              onPress={() => {
                setExporterId('');
                setStatus('');
                setMode('');
                setSearchInput('');
                setSearch('');
                setOffset(0);
              }}
            >
              Limpiar filtros
            </Button>
          )}
        </CardHeader>
        <CardBody className='overflow-x-auto px-0 pb-0'>
          <Table
            removeWrapper
            aria-label='Journeys ShipsGo'
            classNames={{ th: 'bg-default-50', td: 'align-top py-4' }}
          >
            <TableHeader>
              <TableColumn>EXPORTADORA / IDENTIFICADOR</TableColumn>
              <TableColumn>ESTADO</TableColumn>
              <TableColumn>SHIPSGO</TableColumn>
              <TableColumn>RUTA / FECHAS</TableColumn>
              <TableColumn>DESPACHOS</TableColumn>
              <TableColumn>SALUD</TableColumn>
            </TableHeader>
            <TableBody
              isLoading={loading}
              loadingContent={<Spinner label='Cargando journeys…' />}
              emptyContent='No hay journeys para estos filtros.'
            >
              {(data?.journeys ?? []).map(renderJourney)}
            </TableBody>
          </Table>
          <div className='flex flex-col gap-2 border-t border-divider px-5 py-4 sm:flex-row sm:items-center sm:justify-between'>
            <p className='text-xs text-default-500'>
              {total === 0
                ? 'Sin resultados'
                : `${number(offset + 1)}–${number(
                    Math.min(offset + PAGE_SIZE, total)
                  )} de ${number(total)}`}
            </p>
            <div className='flex items-center gap-2'>
              <Button
                size='sm'
                variant='flat'
                isDisabled={offset === 0 || loading}
                onPress={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              >
                Anterior
              </Button>
              <span className='text-xs text-default-500'>
                Página {number(currentPage)} de {number(totalPages)}
              </span>
              <Button
                size='sm'
                variant='flat'
                isDisabled={offset + PAGE_SIZE >= total || loading}
                onPress={() => setOffset(offset + PAGE_SIZE)}
              >
                Siguiente
              </Button>
            </div>
          </div>
        </CardBody>
      </Card>

      <Card shadow='sm'>
        <CardHeader className='flex-col items-start gap-1 px-5 pt-5'>
          <h2 className='text-lg font-semibold'>Historial de ejecuciones</h2>
          <p className='text-sm text-default-500'>
            Hasta 150 ejecuciones de los últimos 14 días; incluye ejecuciones
            vacías y fallos antes de contactar ShipsGo.
          </p>
        </CardHeader>
        <CardBody className='overflow-x-auto px-0 pb-0'>
          <Table
            removeWrapper
            aria-label='Historial de workers ShipsGo'
            classNames={{ th: 'bg-default-50' }}
          >
            <TableHeader>
              <TableColumn>WORKER</TableColumn>
              <TableColumn>INICIO</TableColumn>
              <TableColumn>DURACIÓN</TableColumn>
              <TableColumn>RESULTADOS</TableColumn>
              <TableColumn>ESTADO</TableColumn>
              <TableColumn>DETALLE</TableColumn>
            </TableHeader>
            <TableBody emptyContent='Todavía no hay ejecuciones registradas.'>
              {(data?.runs ?? []).map((run) => (
                <TableRow key={run.id}>
                  <TableCell>
                    <p className='font-medium capitalize'>{run.worker_type}</p>
                    <p className='text-xs text-default-500'>
                      {run.trigger_source}
                    </p>
                  </TableCell>
                  <TableCell>
                    <p className='text-sm'>{formatDateTime(run.started_at)}</p>
                    <p className='text-xs text-default-500'>
                      {relativeTime(run.started_at)}
                    </p>
                  </TableCell>
                  <TableCell>{duration(run)}</TableCell>
                  <TableCell>
                    <p className='text-sm'>
                      {number(run.selected_count)} seleccionados
                    </p>
                    <p className='text-xs text-default-500'>
                      {number(run.succeeded_count)} OK ·{' '}
                      {number(run.failed_count)} fallidos
                    </p>
                  </TableCell>
                  <TableCell>
                    <Chip size='sm' color={runColor(run.status)} variant='flat'>
                      {run.status}
                    </Chip>
                  </TableCell>
                  <TableCell>
                    <div className='max-w-80 text-xs'>
                      <p className={run.error_message ? 'text-danger' : ''}>
                        {run.error_message ??
                          metadataCounts(run) ??
                          'Sin errores'}
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardBody>
      </Card>
    </PageLayout>
  );
};

export default ShipsGoTrackingOperationsPage;
